import { Icon } from '../../../../widgets/icon';
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import ChevronLeftIcon from 'src/shared/icons/chevron-left.svg';
import ChevronRightIcon from 'src/shared/icons/chevron-right.svg';
import styled from 'styled-components';

const CarouselWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const CarouselArrowButton = styled.button<{ $visible: boolean; $position: 'left' | 'right' }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    cursor: pointer;
    border-radius: 50%;
    flex-shrink: 0;
    opacity: ${(props) => (props.$visible ? 1 : 0)};
    pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};
    transition:
        background 0.2s ease,
        opacity 0.2s ease;
    z-index: 5;

    &:hover {
        background: #e5e7eb;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        min-width: 0;
        fill: currentColor;
    }
`;

const ScrollContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scroll-behavior: smooth;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

export interface CarouselProps {
    children: ReactNode;
    /** Gap between items in pixels */
    gap?: number;
    /** Amount to scroll when clicking arrows */
    scrollAmount?: number;
    /** Variant style: 'default' for inline arrows, 'overlay' for absolute positioned arrows */
    variant?: 'default' | 'overlay';
    /** Additional class name for the wrapper */
    className?: string;
    /** Ref to access the scroll container */
    scrollRef?: React.RefObject<HTMLDivElement>;
    /** Content rendered between the scroll area and the right arrow */
    rightExtra?: ReactNode;
}

export const Carousel: FC<CarouselProps> = ({ children, gap = 8, scrollAmount = 150, className, scrollRef: externalScrollRef, rightExtra }) => {
    const internalScrollRef = useRef<HTMLDivElement>(null);
    const scrollRef = externalScrollRef || internalScrollRef;
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkScrollButtons = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
    }, [scrollRef]);

    useEffect(() => {
        // Initial check
        checkScrollButtons();

        // Use ResizeObserver for more reliable size change detection
        const container = scrollRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            checkScrollButtons();
        });

        resizeObserver.observe(container);
        container.addEventListener('scroll', checkScrollButtons);
        window.addEventListener('resize', checkScrollButtons);

        // Also check after a short delay to handle dynamic content
        const timeoutId = setTimeout(checkScrollButtons, 100);

        return () => {
            resizeObserver.disconnect();
            container.removeEventListener('scroll', checkScrollButtons);
            window.removeEventListener('resize', checkScrollButtons);
            clearTimeout(timeoutId);
        };
    }, [checkScrollButtons, scrollRef]);

    // Re-check when children change
    useEffect(() => {
        checkScrollButtons();
    }, [children, checkScrollButtons]);

    const handleScrollLeft = useCallback(() => {
        const container = scrollRef.current;
        if (container) {
            container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
    }, [scrollRef, scrollAmount]);

    const handleScrollRight = useCallback(() => {
        const container = scrollRef.current;
        if (container) {
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    }, [scrollRef, scrollAmount]);

    return (
        <CarouselWrapper className={className}>
            <CarouselArrowButton $visible={showLeftArrow} $position='left' onClick={handleScrollLeft} aria-label={T._('Scroll left', 'Admin')}>
                <ChevronLeftIcon />
            </CarouselArrowButton>
            <ScrollContainer ref={scrollRef as React.RefObject<HTMLDivElement>} style={{ gap }}>
                {children}
            </ScrollContainer>
            <CarouselArrowButton $visible={showRightArrow} $position='right' onClick={handleScrollRight} aria-label={T._('Scroll right', 'Admin')}>
                <ChevronRightIcon />
            </CarouselArrowButton>
            {rightExtra}
        </CarouselWrapper>
    );
};

export default Carousel;
